/**
 * `/notifications/rules` — which events reach which channel.
 *
 * One dialog serves creating and editing, because the fields are the same six
 * either way and two near-identical forms drift apart.
 *
 * Editing **sends only what changed**. The server audits both sides of every
 * field it is given, so a form that posted all six every time would make "this
 * now routes somewhere else" and "somebody fixed a typo" read alike in the log.
 *
 * The event field is offered as a list of the templates the build defines, plus
 * free text — a rule may name a family with a trailing star (`resource.grant.*`)
 * that no single template matches.
 */
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RuleIcon from '@mui/icons-material/Rule';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type {
  AudienceKind,
  ChannelDto,
  RuleDto,
  Severity,
  SubjectDto,
  UpdateRuleRequest,
} from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorState } from '../../components/ErrorState';
import { FormDialog } from '../../components/FormDialog';
import { PageHeader } from '../../components/PageHeader';
import { SubjectPicker } from '../../components/SubjectPicker';
import { subjectKey } from '../../utils/subject';
import { useAsync } from '../../hooks/useAsync';
import { useCrudDialogs } from '../../hooks/useCrudDialogs';
import { useSubjectSearch } from '../../hooks/useSubjectSearch';
import { useListQuery } from '../../hooks/useListQuery';
import { useSubmit } from '../../hooks/useSubmit';
import { useAuth } from '../../auth/useAuth';
import { searchSubjects } from '../../api/identity';
import {
  createRule,
  deleteRule,
  listAudienceResolvers,
  listChannels,
  listRules,
  listTemplates,
  listTransports,
  setRuleEnabled,
  updateRule,
} from '../../api/notifications';

const SEVERITIES: Severity[] = ['info', 'warning', 'critical'];

const AUDIENCE_KINDS: AudienceKind[] = ['channel', 'subject', 'resolver'];

/**
 * Whether two event patterns can ever describe the same event.
 *
 * Mirrors `patternsOverlap` on the server. Duplicated deliberately and kept to
 * six lines: the alternative is a round trip on every keystroke in the event
 * box, and the server still refuses a combination this lets through.
 */
function overlaps(a: string, b: string): boolean {
  const aBase = a.endsWith('*') ? a.slice(0, -1) : a;
  const bBase = b.endsWith('*') ? b.slice(0, -1) : b;
  if (a.endsWith('*') && b.endsWith('*')) return aBase.startsWith(bBase) || bBase.startsWith(aBase);
  if (a.endsWith('*')) return bBase.startsWith(aBase);
  if (b.endsWith('*')) return aBase.startsWith(bBase);
  return aBase === bBase;
}

/** Severity labels, keyed by the API value. The value is the identifier. */
const SEVERITY_KEYS = {
  info: 'severity.info',
  warning: 'severity.warning',
  critical: 'severity.critical',
} as const;

type SeverityKey = (typeof SEVERITY_KEYS)[keyof typeof SEVERITY_KEYS];

function severityKeyFor(severity: string): SeverityKey | undefined {
  return (SEVERITY_KEYS as Record<string, SeverityKey | undefined>)[severity];
}

/** The fields a rule has. The dialog edits this, whichever mode it is in. */
interface RuleForm {
  name: string;
  event: string;
  channelId: number;
  minSeverity: Severity;
  coalesceSeconds: number;
  isEnabled: boolean;
  audienceKind: AudienceKind;
  audienceRef: string;
  excludeActor: boolean;
}

const BLANK_FORM: RuleForm = {
  name: '',
  event: '',
  channelId: 0,
  minSeverity: 'info',
  coalesceSeconds: 0,
  isEnabled: true,
  // The destination itself: what every rule did before audiences existed, and
  // still the right default for an ops channel.
  audienceKind: 'channel',
  audienceRef: '',
  excludeActor: true,
};

function formFor(rule: RuleDto): RuleForm {
  return {
    name: rule.name,
    event: rule.event,
    channelId: rule.channelId,
    minSeverity: rule.minSeverity,
    coalesceSeconds: rule.coalesceSeconds,
    isEnabled: rule.isEnabled,
    audienceKind: rule.audienceKind,
    audienceRef: rule.audienceRef ?? '',
    excludeActor: rule.excludeActor,
  };
}

/** `user:7` → a subject the picker can show, without a round trip for the name. */
function subjectFromRef(ref: string): SubjectDto | null {
  const [type, rawId] = ref.split(':');
  const id = Number(rawId);
  if ((type !== 'user' && type !== 'group') || !Number.isInteger(id) || id <= 0) return null;
  // The label is the reference until the picker searches; a stored rule holds
  // an id, and looking every one up to render a dialog nobody opened is waste.
  return { type, id, displayName: ref };
}

/**
 * What the form changed, and nothing else.
 *
 * The server audits both sides of every field it is handed, so sending an
 * untouched `channelId` back would put "channel 2 → channel 2" in the log next
 * to the one field that really moved.
 */
function changesBetween(original: RuleDto, next: RuleForm): UpdateRuleRequest {
  const before = formFor(original);
  const changes: UpdateRuleRequest = {};

  if (next.name.trim() !== before.name) changes.name = next.name.trim();
  if (next.event.trim() !== before.event) changes.event = next.event.trim();
  if (next.channelId !== before.channelId) changes.channelId = next.channelId;
  if (next.minSeverity !== before.minSeverity) changes.minSeverity = next.minSeverity;
  if (next.coalesceSeconds !== before.coalesceSeconds) {
    changes.coalesceSeconds = next.coalesceSeconds;
  }
  if (next.audienceKind !== before.audienceKind) changes.audienceKind = next.audienceKind;
  if (next.audienceRef !== before.audienceRef) {
    // `channel` stores no reference; sending '' would look like a change to none.
    changes.audienceRef = next.audienceKind === 'channel' ? null : next.audienceRef;
  }
  if (next.excludeActor !== before.excludeActor) changes.excludeActor = next.excludeActor;

  return changes;
}

export default function RulesPage() {
  const { t } = useTranslation('notifications');
  const { can } = useAuth();

  /** Falls back to the raw value for a severity this build does not know. */
  function severityLabel(severity: string): string {
    const key = severityKeyFor(severity);
    return key ? t(key) : severity;
  }
  const list = useListQuery({ defaultSort: 'event' });
  const rules = useAsync(() => listRules(list.query), [list.key]);
  // The channel list here is a lookup for the picker and for naming a rule's
  // destination, not a paged view — so it asks for the endpoint's maximum in
  // one go. A deployment with more channels than that has outgrown a dropdown.
  const channels = useAsync(() => listChannels({ limit: 200 }), []);
  const transports = useAsync(() => listTransports(), []);
  const templates = useAsync(() => listTemplates(), []);
  const resolvers = useAsync(() => listAudienceResolvers(), []);

  // The subject search for a `subject` audience. Debounced in the hook, not in
  // the picker — same arrangement as the share dialog.
  const foundSubjects = useSubjectSearch(searchSubjects);

  const mayManage = can('notification.manage');

  const crud = useCrudDialogs<RuleDto, RuleForm>({ blank: BLANK_FORM, formFor });
  const { form, editing, deleting } = crud;

  // Template events, de-duplicated. Several templates can answer to one event,
  // and a template with no event is a manual one that no rule can match.
  const eventOptions = [
    ...new Set(
      (templates.data?.templates ?? [])
        .map((template) => template.event)
        .filter((event): event is string => event !== null),
    ),
  ].sort();

  const create = useSubmit(async () => {
    const created = await createRule({
      name: form.name.trim(),
      event: form.event.trim(),
      channelId: form.channelId,
      minSeverity: form.minSeverity,
      coalesceSeconds: form.coalesceSeconds,
      isEnabled: form.isEnabled,
      audienceKind: form.audienceKind,
      audienceRef: form.audienceKind === 'channel' ? null : form.audienceRef,
      excludeActor: form.excludeActor,
    });
    rules.reload();
    return created;
  });

  const save = useSubmit(async () => {
    if (!editing) return undefined;
    const changes = changesBetween(editing, form);
    // An empty PATCH is refused by the server rather than writing an audit row
    // for a change that did not happen, so the dialog does not send one.
    if (Object.keys(changes).length === 0) return { rule: editing };

    const result = await updateRule(editing.id, changes);
    rules.reload();
    return result;
  });

  const toggle = useSubmit(async (rule: RuleDto) => {
    const result = await setRuleEnabled(rule.id, !rule.isEnabled);
    rules.reload();
    return result;
  });

  const remove = useSubmit(async () => {
    if (!deleting) return undefined;
    const result = await deleteRule(deleting.id);
    crud.closeDelete();
    rules.reload();
    return result;
  });

  /**
   * The resolvers this rule's event could actually use.
   *
   * Offering one that cannot answer would produce a rule that fires and tells
   * nobody — which is exactly the failure the server refuses, so the dialog
   * should not let it be built. The overlap test lives on the server; here it is
   * a prefix comparison against the patterns it published.
   */
  /**
   * Whether a channel can carry a rule with this audience: aimed at a person
   * for a subject or a resolver, at its own room for "the channel itself".
   *
   * From each transport's `addressing` as the server reports it for this build —
   * a Rocket.Chat channel takes a person only when a module provides usernames.
   * The server refuses a per-person rule on the rest; this keeps them out of
   * the list. Unknown until the transports load, and then everything is offered.
   */
  function carries(channel: ChannelDto, kind: AudienceKind): boolean {
    const transport = transports.data?.transports.find((one) => one.key === channel.transport);
    return transport === undefined || transport.addressing.includes(kind === 'channel' ? 'broadcast' : 'user');
  }

  function resolversForEvent(event: string): { key: string; label: string }[] {
    const pattern = event.trim();
    if (pattern === '') return resolvers.data?.resolvers ?? [];

    return (resolvers.data?.resolvers ?? []).filter((resolver) =>
      resolver.events.some((supported) => overlaps(supported, pattern)),
    );
  }

  function channelName(channelId: number): string {
    return channels.data?.channels.find((channel) => channel.id === channelId)?.name ?? `#${channelId}`;
  }

  return (
    <>
      <PageHeader
        title={t('rules.title')}
        description={t('rules.description')}
        actions={
          mayManage && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() =>
                crud.openCreate({ channelId: channels.data?.channels[0]?.id ?? 0 })
              }
            >
              {t('rules.new')}
            </Button>
          )
        }
      />

      {toggle.error && <ErrorState error={toggle.error} />}

      <AsyncContent state={rules}>
        {({ rules: rows, page }) => (
          <EntityTable
            rows={rows}
            rowKey={(rule) => rule.id}
            list={list}
            page={page}
            empty={{
              icon: <RuleIcon fontSize="inherit" />,
              title: t('rules.emptyTitle'),
              description: t('rules.emptyDescription'),
            }}
            columns={[
              {
                field: 'name',
                label: t('rules.column.name'),
                sortable: true,
                render: (rule) => (
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {rule.name}
                  </Typography>
                ),
              },
              {
                field: 'event',
                label: t('rules.column.event'),
                sortable: true,
                render: (rule) => (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={rule.event}
                    sx={{ fontFamily: 'monospace', fontSize: 11 }}
                  />
                ),
              },
              // The channel column is not sortable: a rule stores a channel id,
              // and the server declines to join the channel table into a query
              // whose job is to stay small.
              {
                field: 'channel',
                label: t('rules.column.channel'),
                width: 160,
                render: (rule) => (
                  <Typography variant="body2" color="text.secondary">
                    {channelName(rule.channelId)}
                  </Typography>
                ),
              },
              // Who it tells, which is as much a disclosure decision as where.
              {
                field: 'audience',
                label: t('rules.column.audience'),
                width: 170,
                render: (rule) => {
                  if (rule.audienceKind === 'channel') {
                    return (
                      <Typography variant="caption" color="text.secondary">
                        {t('rules.audience.channel')}
                      </Typography>
                    );
                  }

                  const label =
                    rule.audienceKind === 'resolver'
                      ? (resolvers.data?.resolvers.find((one) => one.key === rule.audienceRef)
                          ?.label ?? rule.audienceRef)
                      : rule.audienceRef;

                  return (
                    <Stack spacing={0.25}>
                      <Typography variant="caption">{label}</Typography>
                      {!rule.excludeActor && (
                        <Typography variant="caption" color="text.secondary">
                          {t('rules.audience.includesActor')}
                        </Typography>
                      )}
                    </Stack>
                  );
                },
              },
              {
                field: 'minSeverity',
                label: t('rules.column.minSeverity'),
                width: 110,
                render: (rule) => (
                  <Typography variant="caption" color="text.secondary">
                    {severityLabel(rule.minSeverity)}
                  </Typography>
                ),
              },
              {
                field: 'coalesce',
                label: t('rules.column.coalesce'),
                width: 110,
                render: (rule) => (
                  <Typography variant="caption" color="text.secondary">
                    {rule.coalesceSeconds === 0
                      ? t('rules.everyMessage')
                      : t('rules.coalesceSeconds', { count: rule.coalesceSeconds })}
                  </Typography>
                ),
              },
              {
                field: 'enabled',
                label: t('rules.column.enabled'),
                width: 90,
                render: (rule) => (
                  <Switch
                    size="small"
                    checked={rule.isEnabled}
                    disabled={!mayManage || toggle.pending}
                    onChange={() => void toggle.submit(rule)}
                  />
                ),
              },
            ]}
            actions={(rule) => [
              {
                key: 'edit',
                label: t('rules.edit'),
                icon: <EditOutlinedIcon fontSize="small" />,
                onClick: () => crud.openEdit(rule),
                hidden: !mayManage,
              },
              {
                key: 'delete',
                label: t('rules.delete'),
                icon: <DeleteOutlinedIcon fontSize="small" />,
                tone: 'error',
                onClick: () => crud.openDelete(rule),
                hidden: !mayManage,
              },
            ]}
          />
        )}
      </AsyncContent>

      <FormDialog
        open={crud.isOpen}
        title={editing ? t('rules.dialog.editTitle', { name: editing.name }) : t('rules.dialog.title')}
        submitLabel={editing ? t('rules.dialog.save') : t('rules.dialog.submit')}
        pending={create.pending || save.pending}
        error={editing ? save.error : create.error}
        disabled={
          form.name.trim() === '' ||
          form.event.trim() === '' ||
          form.channelId === 0 ||
          (form.audienceKind !== 'channel' && form.audienceRef.trim() === '')
        }
        onClose={crud.close}
        onSubmit={async () => {
          if (await (editing ? save.submit() : create.submit())) crud.close();
        }}
      >
        {editing && (
          <Alert severity="info" variant="outlined">
            {t('rules.dialog.routingNotice')}
          </Alert>
        )}

        <TextField
          label={t('rules.dialog.name')}
          value={form.name}
          onChange={(event) => crud.patch({ name: event.target.value })}
          autoFocus
          fullWidth
        />

        <Autocomplete
          freeSolo
          options={eventOptions}
          value={form.event}
          onInputChange={(_event, value) => crud.patch({ event: value })}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('rules.dialog.event')}
              placeholder="resource.grant.*"
              helperText={t('rules.dialog.eventHelp')}
            />
          )}
        />

        <TextField
          select
          label={t('rules.dialog.channel')}
          value={form.channelId || ''}
          onChange={(event) => crud.patch({ channelId: Number(event.target.value) })}
          fullWidth
        >
          {(channels.data?.channels ?? [])
            // The rule's own channel stays, so editing an old rule never blanks the field.
            .filter((channel) => channel.id === form.channelId || carries(channel, form.audienceKind))
            .map((channel) => (
              <MenuItem key={channel.id} value={channel.id}>
                {channel.name}
                {!channel.isEnabled && t('rules.dialog.channelDisabled')}
              </MenuItem>
            ))}
        </TextField>

        <TextField
          select
          label={t('rules.dialog.audienceKind')}
          value={form.audienceKind}
          onChange={(event) => {
            const kind = event.target.value as AudienceKind;
            const channel = channels.data?.channels.find((one) => one.id === form.channelId);
            // Changing the kind clears the reference: a `user:7` left behind
            // under a resolver audience is a rule that means something else.
            // It clears a channel that cannot carry the new audience as well.
            crud.patch({
              audienceKind: kind,
              audienceRef: '',
              ...(channel !== undefined && !carries(channel, kind) ? { channelId: 0 } : {}),
            });
          }}
          helperText={t('rules.dialog.audienceHelp')}
          fullWidth
        >
          {AUDIENCE_KINDS.map((kind) => (
            <MenuItem key={kind} value={kind}>
              {t(`rules.audienceKind.${kind}` as 'rules.audienceKind.channel')}
            </MenuItem>
          ))}
        </TextField>

        {form.audienceKind === 'subject' && (
          <SubjectPicker
            options={foundSubjects.options}
            value={subjectFromRef(form.audienceRef)}
            loading={foundSubjects.loading}
            onChange={(subject) =>
              crud.patch({ audienceRef: subject ? subjectKey(subject) : '' })
            }
            onQueryChange={foundSubjects.setQuery}
            label={t('rules.dialog.audienceSubject')}
          />
        )}

        {form.audienceKind === 'resolver' && (
          <TextField
            select
            label={t('rules.dialog.audienceResolver')}
            value={form.audienceRef}
            onChange={(event) => crud.patch({ audienceRef: event.target.value })}
            helperText={
              resolversForEvent(form.event).length === 0
                ? t('rules.dialog.audienceNoneForEvent')
                : t('rules.dialog.audienceResolverHelp')
            }
            fullWidth
          >
            {resolversForEvent(form.event).map((resolver) => (
              <MenuItem key={resolver.key} value={resolver.key}>
                {resolver.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        {form.audienceKind !== 'channel' && (
          <FormControlLabel
            control={
              <Switch
                checked={!form.excludeActor}
                onChange={(event) => crud.patch({ excludeActor: !event.target.checked })}
              />
            }
            label={t('rules.dialog.includeActor')}
          />
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label={t('rules.dialog.minSeverity')}
            value={form.minSeverity}
            onChange={(event) => crud.patch({ minSeverity: event.target.value as Severity })}
            fullWidth
          >
            {SEVERITIES.map((severity) => (
              <MenuItem key={severity} value={severity}>
                {severityLabel(severity)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="number"
            label={t('rules.dialog.coalesceSeconds')}
            value={form.coalesceSeconds}
            onChange={(event) =>
              crud.patch({ coalesceSeconds: Math.max(0, Number(event.target.value)) })
            }
            helperText={t('rules.dialog.coalesceHelp')}
            fullWidth
          />
        </Stack>
        <Box />
      </FormDialog>

      <ConfirmDialog
        open={deleting !== null}
        title={t('rules.deleteDialog.title', { name: deleting?.name ?? '' })}
        tone="error"
        confirmLabel={t('rules.deleteDialog.confirm')}
        pending={remove.pending}
        error={remove.error}
        onClose={crud.closeDelete}
        onConfirm={() => void remove.submit()}
        message={t('rules.deleteDialog.message', {
          event: deleting?.event ?? t('rules.deleteDialog.thisRule'),
          channel: deleting
            ? channelName(deleting.channelId)
            : t('rules.deleteDialog.thatChannel'),
        })}
      />
    </>
  );
}
